/**
 * PublicFooter — the footer every public page carries, chosen by the address
 * the page is served on (spec 024; the rule #189 set for the landing).
 *
 *   - A brokerage's address (its platform subdomain or a verified custom
 *     domain) → that brokerage's ComplianceFooter, from admin data.
 *   - An agent's own custom domain → the agent's footer; if the agent lookup
 *     fails, the brokerage's footer. by-domain returns the agent's users.id
 *     while the agent-landing route expects agents.id (a known gap, pinned by
 *     test.fail() in tenant-footer.spec.ts) — the visitor must still see the
 *     broker, never the shared footer.
 *   - Any other address, or a lookup that fails → the shared footer: the Equal
 *     Housing notice and the product's copyright, naming no broker. A failure
 *     errs towards no broker information, never wrong information.
 *
 * Nothing renders while the address is being resolved, so a brokerage's
 * visitor never sees the shared footer flash first.
 *
 * Not mounted in AuthLayout: that layout also hosts /b/:slug and the agent
 * page, which carry their own ComplianceFooter.
 */
import { useQuery } from "@tanstack/react-query";

import { APP_NAME } from "../brand";
import { ComplianceFooter } from "../features/auth/ComplianceFooter";
import { identityApi } from "../features/auth/passkeyApi";
import { useHostBrokerage } from "../features/home/customDomain";
import { EqualHousingNotice } from "./EqualHousing";

import "./PublicFooter.css";

export function PublicFooter() {
  const host = useHostBrokerage(true);
  const slug = host.kind === "brokerage" ? host.slug : null;
  const agentId = host.kind === "brokerage" ? host.agentId : undefined;

  const agentLookup = useQuery({
    queryKey: ["agent-landing", slug, agentId],
    queryFn: () => identityApi.getAgentLanding(slug!, agentId!),
    enabled: Boolean(slug && agentId),
    // A 404 is an answer (see useHostBrokerage): never refetched on mount.
    retry: false,
    retryOnMount: false,
    staleTime: Infinity,
  });
  const tenantLookup = useQuery({
    queryKey: ["tenant-by-slug", slug],
    queryFn: () => identityApi.getTenantBySlug(slug!),
    enabled: Boolean(slug) && (!agentId || agentLookup.isError),
    retry: false,
    retryOnMount: false,
    staleTime: Infinity,
  });

  if (host.kind === "checking") return null;
  if (slug) {
    if (agentId) {
      if (agentLookup.data?.tenant.brokerage) {
        return (
          <ComplianceFooter
            tenant={agentLookup.data.tenant}
            agent={agentLookup.data.agent}
          />
        );
      }
      if (!agentLookup.isError) return null;
    }
    if (tenantLookup.isPending) return null;
    if (tenantLookup.data?.brokerage) {
      return <ComplianceFooter tenant={tenantLookup.data} />;
    }
  }
  return <SharedFooter />;
}

/** Names no broker: an address no brokerage owns, or one we could not resolve. */
function SharedFooter() {
  return (
    <footer className="cw-footer">
      <div className="cw-footer__row">
        <EqualHousingNotice className="cw-footer__eho" />
      </div>
      <div className="cw-footer__copy">
        © {new Date().getFullYear()} {APP_NAME}
      </div>
    </footer>
  );
}
